import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { http } from '../api/client.tsx'
import { Endpoints } from '../api/endpoints.tsx'

export type WorkspaceMode = 'personal' | 'family'

export type FamilySpaceMember = {
  userId: string
  role: string
  joinedAt: string
}

export type FamilySpace = {
  id: string
  name: string
  ownerId: string
  createdAt: string
  members: FamilySpaceMember[]
}

type FamilyStore = {
  mode: WorkspaceMode
  space: FamilySpace | null
  loading: boolean

  setMode: (mode: WorkspaceMode) => void
  fetchSpace: () => Promise<void>
  createSpace: (name: string) => Promise<string>
  inviteToFamily: (email?: string) => Promise<string>
  joinFamily: (token: string) => Promise<void>
  leaveFamily: () => Promise<void>
  removeMember: (memberUserId: string) => Promise<void>
  dissolveFamily: () => Promise<void>
  clearSpace: () => void
}

export const useFamilyStore = create<FamilyStore>()(
  persist(
    (set, get) => ({
      mode: 'personal',
      space: null,
      loading: false,

      setMode: (mode) => set({ mode }),

      fetchSpace: async () => {
        set({ loading: true })
        try {
          const space = await http.get<FamilySpace>(Endpoints.family.space, undefined, true)
          set({ space, loading: false })
        } catch {
          set({ space: null, loading: false })
        }
      },

      createSpace: async (name: string) => {
        const id = await http.post<string>(Endpoints.family.space, { name })
        await get().fetchSpace()
        return id
      },

      inviteToFamily: async (email?: string) => {
        const link = await http.post<string>(Endpoints.family.invite, { email: email ?? null })
        return link
      },

      joinFamily: async (token: string) => {
        await http.post(Endpoints.family.join, { token })
        await get().fetchSpace()
      },

      leaveFamily: async () => {
        await http.post(Endpoints.family.leave)
        set({ space: null, mode: 'personal' })
      },

      removeMember: async (memberUserId: string) => {
        await http.del(Endpoints.family.removeMember(memberUserId))
        await get().fetchSpace()
      },

      dissolveFamily: async () => {
        await http.del(Endpoints.family.space)
        set({ space: null, mode: 'personal' })
      },

      clearSpace: () => set({ space: null, mode: 'personal' }),
    }),
    { name: 'family-storage', partialize: (s) => ({ mode: s.mode }) }
  )
)
